import uuid from 'react-uuid';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Box from '@mui/material/Box';
import { FieldProps } from '@rjsf/utils';
import { toast } from 'react-toastify';

export const validateArrayOfString = (data: any) => {
  return Array.isArray(data) && data.every((item) => typeof item === 'string');
};

export const validateKeywordLabelMap = (data: any) => {
  // Check if data is an object
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return false;
  }

  // Check if each property is an array of strings
  for (const key in data) {
    if (!validateArrayOfString(data[key])) {
      return false;
    }
  }

  return true;
};

export const validateStringMap = (data: any, n?: number) => {
  // Check if data is an object
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return false;
  }

  // Check if the object has exactly n properties, if n is provided
  if (n !== undefined && Object.keys(data).length !== n) {
    return false;
  }

  // Check if each property is a string
  for (const key in data) {
    if (typeof data[key] !== 'string') {
      return false;
    }
  }

  return true;
};

export const validateArrayOfStringMap = (data: any, n?: number) => {
  // Check if data is an array
  if (!Array.isArray(data)) {
    return false;
  }

  // Check if each item is an object with string key-value pairs
  for (const item of data) {
    if (!validateStringMap(item, n)) {
      return false;
    }
  }

  return true;
};

export interface FieldWithUploadProps extends FieldProps {
  buttonText: string;
  validate: (data: any) => boolean;
  fieldType: 'array' | 'object';
}

const FieldWithUpload = ({ buttonText = 'Upload Values', validate, fieldType, ...props }: FieldWithUploadProps) => {
  const id = uuid();

  const FieldComponent = props.registry.fields[fieldType === 'array' ? 'ArrayField' : 'ObjectField'];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          try {
            const data = JSON.parse(result);
            if (validate(data)) {
              props.onChange(data);
            } else {
              toast.error('Validation failed. The JSON structure is not as expected.');
              return;
            }
            props.onChange(data);
          } catch (error) {
            toast.error('Error reading file. Make sure it is a valid JSON file.');
          }
        }
      };
      reader.readAsText(file);
    }

    // Reset the value of the file input to ensure onChange is fired next time the same file is selected
    event.target.value = '';
  };

  return (
    <div>
      <input
        type="file"
        id={id}
        onChange={handleFileChange}
        accept=".json"
        style={{ display: 'none' }}
        disabled={props.disabled}
      />
      <label htmlFor={id}>
        <Button variant="contained" component="span" startIcon={<CloudUploadIcon />} disabled={props.disabled}>
          {buttonText}
        </Button>
      </label>
      <Box mt={2}>
        <FieldComponent {...props} />
      </Box>
    </div>
  );
};

export default FieldWithUpload;
